// Assessment.js — Pre-payment wellness assessment with AI insights
import React, { useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db, auth } from "./firebase";

function CatIcon({ id }) {
  const s = { viewBox:"0 0 16 16", width:16, height:16, fill:"none", stroke:"currentColor", strokeWidth:1.5, strokeLinecap:"round" };
  if (id === "ground") return <svg {...s}><line x1="8" y1="2" x2="8" y2="10"/><line x1="4" y1="6" x2="8" y2="10"/><line x1="12" y1="6" x2="8" y2="10"/><line x1="3" y1="13" x2="13" y2="13"/></svg>;
  if (id === "past")   return <svg {...s}><circle cx="8" cy="8" r="6.5"/><polyline points="8,5 8,8 10,10"/></svg>;
  if (id === "love")   return <svg {...s}><path d="M8 13s-6-4-6-8a4 4 0 018 0 4 4 0 018 0c0 4-6 8-6 8z" strokeLinejoin="round"/></svg>;
  if (id === "future") return <svg {...s}><circle cx="8" cy="8" r="6.5"/><line x1="8" y1="1.5" x2="8" y2="8"/><line x1="8" y1="8" x2="11" y2="11"/></svg>;
  return <svg {...s}><circle cx="8" cy="8" r="6.5"/></svg>;
}

// ─── QUESTIONS ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: "grounding",
    sw: "Uko Grounded?",
    en: "Are You Grounded?",
    icon: "ground",
    questions: [
      { sw: "Je, mara nyingi huwa unajikuta ukitawaliwa na mawazo?", en: "Do you often find yourself living in your head more than in your body?" },
      { sw: "Je, ukikaa kimya bila simu kwa dakika 10, unajisikia hali ya wasiwasi?", en: "Does sitting in silence without your phone for 10 minutes make you feel anxious?" },
      { sw: "Je, mara nyingi huwa unajisahau kama mwili wako unahitaji kupumzika, chakula au kuwa peke yako?", en: "Do you often not know when your body needs rest, food, or space?" },
      { sw: "Je, mawazo yako mara nyingi yako kwenye past au future badala ya wakati wa sasa?", en: "Are your thoughts mostly in the past or future rather than the present moment?" },
      { sw: "Je, unahisi maisha mara nyingi yanakutokea tu (unapelekwa pelekwa na maisha), badala ya wewe kuwa na control nayo?", en: "Do you feel like life mostly happens to you rather than you being in control?" },
    ],
  },
  {
    id: "past",
    sw: "Umeshikilia sana Past?",
    en: "Holding On to the Past?",
    icon: "past",
    questions: [
      { sw: "Je, kuna mtu au tukio la zamani ambalo bado linakuchochea/kuamsha hisia kali?", en: "Is there a person or past event that still triggers strong emotions in you?" },
      { sw: "Je, unaona unarudia patterns zile zile katika mahusiano au maisha yako?", en: "Do you notice yourself repeating the same patterns in your relationships or life?" },
      { sw: "Je, unajiona ukipata ugumu kuamini watu wapya hata wakiwa wema?", en: "Do you find it hard to trust new people even when they seem good?" },
      { sw: "Je, bado unajiita kama mtu aliyeumizwa, kukataliwa, au kushindwa? (Mimi mtu wa kukataliwa tu…nk)", en: "Do you still identify yourself as someone who was hurt, rejected, or failed?" },
      { sw: "Unapofikiria maisha yako ya nyuma, unahisi kama kuna mambo ambayo hayajakamilika?", en: "When you think about your past, does it feel like there is unfinished business?" },
    ],
  },
  {
    id: "selflove",
    sw: "Kiwango cha Self Love (Kujipenda)",
    en: "Level of Self Love",
    icon: "love",
    questions: [
      { sw: "Je, sauti yako ya ndani mara nyingi inakukosoa na kukudharau?", en: "Does your inner voice often criticize and put you down?" },
      { sw: "Je, unashindwa kusema 'hapana' bila kujisikia hatia au hofu ya kupoteza mtu?", en: "Do you struggle to say no without feeling guilty or afraid of losing someone?" },
      { sw: "Je, unahisi unahitaji idhini ya wengine ili ujisikie una thamani (watu wasipoona thamani yako, na wewe unajiona huna)?", en: "Do you feel you need others' approval to feel worthy?" },
      { sw: "Je, mara nyingi huwa unapuuzia swala kujitunza vizuri — usingizi, chakula, au wakati wa kupumzika?", en: "Do you often neglect taking care of yourself — sleep, food, or rest?" },
      { sw: "Je, ukisifiwa au kupewa upendo, huwa unapata shida kuupokea kikamilifu?", en: "When given a compliment or love, do you struggle to fully accept it?" },
    ],
  },
  {
    id: "future",
    sw: "Una Connection na Future?",
    en: "Connected to Your Future?",
    icon: "future",
    questions: [
      { sw: "Huna picha wazi ya maisha unayotaka miaka 3 hadi 5 ijayo?", en: "Do you lack a clear picture of the life you want in the next 3 to 5 years?" },
      { sw: "Je, ukiamka asubuhi, mara nyingi huna kitu kinachokutia hamasa ya kusonga mbele?", en: "When you wake up in the morning, do you often have nothing pulling you forward?" },
      { sw: "Je, mara nyingi unakaa kwenye comfort zone badala ya kuchukua hatua mpya?", en: "Do you often stay in your comfort zone instead of taking new steps forward?" },
      { sw: "Je, ukifikiria future yako, unahisi wasiwasi na kutokuwa na uhakika?", en: "When you think about your future, do you feel anxiety and uncertainty?" },
      { sw: "Je, mwaka huu unaendelea vivyo hivyo bila mabadiliko makubwa toka mwaka uliopita?", en: "Is this year going the same way as last year with no major changes?" },
    ],
  },
];

// YES = 1 point (problem exists), NO = 0 points
// Higher score = more issues = more need for the program
const LABELS = {
  sw: { yes: "Ndiyo", no: "Hapana", skip: "Ruka tathmini", seeResults: "Angalia matokeo yangu", loading: "Inachambua...", title: "Tathmini yako ya kibinafsi", sub: "Jibu kwa uaminifu. Hakuna jibu baya.", answeredOf: "kati ya 20 uliojibu" },
  en: { yes: "Yes", no: "No", skip: "Skip assessment", seeResults: "See my results", loading: "Analyzing...", title: "Your personal assessment", sub: "Answer honestly. There are no wrong answers.", answeredOf: "of 20 answered" },
};

// ─── AI INSIGHTS ──────────────────────────────────────────────────────────────
const getAIInsights = async (scores, lang, userName) => {
  console.log("Calling AI insights...");
  const categoryNames = {
    grounding: lang === "sw" ? "Grounding" : "Grounding",
    past: lang === "sw" ? "Kuachilia Past" : "Releasing the Past",
    selflove: lang === "sw" ? "Self Love" : "Self Love",
    future: lang === "sw" ? "Connection na Future" : "Future Connection",
  };
  const scoreDesc = Object.entries(scores).map(([k, v]) => `${categoryNames[k]}: ${v}/5`).join(", ");
  const weakest = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  const weakestName = categoryNames[weakest[0]];

  const prompt = lang === "sw"
    ? `Wewe ni Coach Elibariki Mkumbo, mtu anayejua sayansi ya akili na roho. Mteja anaitwa ${userName || "rafiki"}. Matokeo yao ya tathmini ni: ${scoreDesc} (alama ya juu = changamoto zaidi). Eneo lao gumu zaidi ni ${weakestName} (${weakest[1]}/5).

Andika ujumbe mfupi wa kibinafsi (paragraphs 3, kila moja mistari 2-3):
1. Paragraph 1: Waambie unachokiona katika matokeo yao — kwa upole na ukweli. Anza na jina lao.
2. Paragraph 2: Eleza jinsi program ya "Brand New Me" inavyoshughulikia hasa eneo lao gumu zaidi.
3. Paragraph 3: Kauli ya nguvu ya kuwahimiza kuchukua hatua — leo, sio kesho.

Tumia lugha ya Kiswahili ya kawaida, ya moyo. Usiandike kichwa cha habari. Usiandike orodha. Paragraphs tu.`
    : `You are Coach Elibariki Mkumbo. The client's name is ${userName || "friend"}. Their assessment results: ${scoreDesc} (higher = more challenges). Most challenging: ${weakestName} (${weakest[1]}/5).

Write exactly 2 short paragraphs (2 sentences each):
1. What you see in their results — gently and honestly. Start with their name.
2. How the "Brand New Me" program addresses their main challenge, and one sentence encouraging them to begin today.

Warm English only. No headings. No lists.`;

  const token = await auth.currentUser?.getIdToken();
  const response = await fetch("/.netlify/functions/aiinsights", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ prompt }),
  });
  const data = await response.json();
  console.log("AI response:", data);
  if (data.error) throw new Error(data.error);
  return data.text || "";
};

// ─── SCORE HELPERS ────────────────────────────────────────────────────────────
const getScoreLevel = (score) => {
  if (score >= 4) return { color: "#E8745A" };
  if (score >= 2) return { color: "#D4A849" };
  return { color: "#6A9E6A" };
};

const getScoreLabel = (score, lang) => {
  if (score >= 4) return lang === "sw" ? "Inahitaji kazi" : "Needs attention";
  if (score >= 2) return lang === "sw" ? "Unakua" : "Growing";
  return lang === "sw" ? "Umepiga hatua" : "Doing well";
};

function AboutIcon({ k }) {
  const s = { viewBox:"0 0 16 16", width:14, height:14, fill:"none", stroke:"currentColor", strokeWidth:1.5 };
  if (k === "cal") return <svg {...s}><rect x="1.5" y="3" width="13" height="12" rx="1.5"/><path d="M5 1.5v3M11 1.5v3M1.5 7h13"/></svg>;
  if (k === "aud") return <svg {...s}><path d="M3 9a5 5 0 0110 0"/><rect x="1" y="9" width="3" height="4" rx="1"/><rect x="12" y="9" width="3" height="4" rx="1"/></svg>;
  if (k === "jnl") return <svg {...s}><rect x="3" y="1" width="10" height="14" rx="1"/><path d="M5 5h6M5 8h6M5 11h4"/></svg>;
  if (k === "phn") return <svg {...s}><rect x="4" y="1" width="8" height="14" rx="2"/><path d="M4 4h8M4 12h8"/></svg>;
  if (k === "lck") return <svg {...s}><rect x="2.5" y="7" width="11" height="8" rx="1.5"/><path d="M5 7V5a3 3 0 016 0v2"/></svg>;
  return null;
}

// ─── ABOUT PROGRAM ────────────────────────────────────────────────────────────
function AboutProgram({ lang, onPay, onBack }) {
  const t = {
    sw: {
      title: "Brand New Me", sub: "Program ya siku 10 ya mabadiliko ya kweli",
      items: [["cal","Siku 10 za mwongozo wa kina"],["aud","Vikao 29 vya audio — asubuhi, mchana & jioni"],["jnl","Journal ya kibinafsi na maoni ya coach"],["phn","Vikumbusho vya WhatsApp & kalenda"],["lck","Ufikiaji wa maisha yote kwa vikao vyako"]],
      price: "TZS 5,000", note: "malipo mara moja · yote ndani", cta: "Niandikishe sasa", back: "← Rudi kwa matokeo",
    },
    en: {
      title: "Brand New Me", sub: "A 10-day program for real transformation",
      items: [["cal","10 days of deep guided meditation"],["aud","29 audio sessions — morning, noon & evening"],["jnl","Personal journal with coach feedback"],["phn","WhatsApp & calendar reminders"],["lck","Lifetime access to your sessions"]],
      price: "TZS 5,000", note: "one-time payment · all inclusive", cta: "Enroll me now", back: "← Back to results",
    },
  };
  const c = t[lang];
  return (
    <div className="about-wrap">
      <button className="assess-back-btn" onClick={onBack}>{c.back}</button>
      <h2 className="about-title">{c.title}</h2>
      <p className="about-sub">{c.sub}</p>
      <div className="about-items">{c.items.map(([icon,text],i) => <div key={i} className="about-item"><span style={{display:"flex",alignItems:"center"}}><AboutIcon k={icon}/></span><span>{text}</span></div>)}</div>
      <div className="about-price"><span className="about-price-amt">{c.price}</span><span className="about-price-note">{c.note}</span></div>
      <button className="assess-primary-btn" onClick={onPay}>{c.cta}</button>
    </div>
  );
}

// ─── RESULTS SCREEN ───────────────────────────────────────────────────────────
function ResultsScreen({ scores, lang, userName, onPay, onSkip, isReassessment = false }) {
  const [insights, setInsights] = useState("");
  const [loadingInsights, setLoadingInsights] = useState(lang === "en");
  const [showAbout, setShowAbout] = useState(false);

  React.useEffect(() => {
    if (lang !== "en") return; // AI insights only in English
    getAIInsights(scores, lang, userName)
      .then(text => { setInsights(text); setLoadingInsights(false); })
      .catch(() => {
        setInsights("Thank you for completing your assessment. Your full personalized insights from Coach Elibariki will be ready after enrolling.");
        setLoadingInsights(false);
      });
  }, []);

  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  const totalPct = Math.round((total / 20) * 100);

  const t = {
    sw: { title: "Matokeo yako", totalLabel: "Dalili zako kwa jumla", freeInsights: isReassessment ? "Maoni ya Coach" : "Maoni ya Coach (bila malipo)", locked: "Maoni kamili — baada ya kujiandikisha", cta: isReassessment ? "← Rudi kwa programu" : "Angalia program →", skip: "Nenda malipo moja kwa moja", outOf: "kati ya 5" },
    en: { title: "Your Results", totalLabel: "Your overall challenges", freeInsights: isReassessment ? "Coach insights" : "Coach insights (free)", locked: "Full detailed insights — after enrolling", cta: isReassessment ? "← Back to program" : "View the program →", skip: "Go straight to payment", outOf: "out of 5" },
  };
  const c = t[lang];

  if (showAbout) return <AboutProgram lang={lang} onPay={onPay} onBack={() => setShowAbout(false)} />;

  return (
    <div className="results-wrap">
      <h2 className="results-title">{c.title}</h2>

      <div className="overall-score-circle">
        <svg viewBox="0 0 120 120" width="110" height="110">
          <circle cx="60" cy="60" r="52" fill="none" stroke="#E3EEF0" strokeWidth="10"/>
          <circle cx="60" cy="60" r="52" fill="none" stroke="#4F9CA8" strokeWidth="10"
            strokeDasharray={`${2*Math.PI*52}`}
            strokeDashoffset={`${2*Math.PI*52*(1-totalPct/100)}`}
            strokeLinecap="round" transform="rotate(-90 60 60)"
            style={{transition:"stroke-dashoffset 1s ease"}}/>
          <text x="60" y="55" textAnchor="middle" fontSize="22" fontWeight="600" fill="#1F4E5A">{total}</text>
          <text x="60" y="72" textAnchor="middle" fontSize="11" fill="#8FA6AD">/20</text>
        </svg>
        <p className="overall-score-label">{c.totalLabel}</p>
      </div>

      <div className="category-scores">
        {CATEGORIES.map(cat => {
          const score = scores[cat.id];
          const { color } = getScoreLevel(score);
          const label = getScoreLabel(score, lang);
          const pct = (score / 5) * 100;
          return (
            <div key={cat.id} className="cat-score-row">
              <div className="cat-score-left">
                <span className="cat-icon"><CatIcon id={cat.icon}/></span>
                <div>
                  <span className="cat-name">{lang==="sw" ? cat.sw : cat.en}</span>
                  <span className="cat-label" style={{color}}>{label}</span>
                </div>
              </div>
              <div className="cat-score-right">
                <div className="cat-bar"><div className="cat-bar-fill" style={{width:`${pct}%`,background:color}}/></div>
                <span className="cat-score-num" style={{color}}>{score}<span className="cat-out-of">/5</span></span>
              </div>
            </div>
          );
        })}
      </div>

      {lang === "en" && (
        <div className="insights-box">
          <div className="insights-label">{c.freeInsights}</div>
          {loadingInsights
            ? <div className="insights-loading"><div className="insights-spinner"/><span>Coach is analyzing…</span></div>
            : <p className="insights-text">{insights}</p>}
        </div>
      )}

      {!isReassessment && <div className="insights-locked"><svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" width="12" height="12" style={{marginRight:5,verticalAlign:"middle",flexShrink:0}}><rect x="2" y="6.5" width="10" height="7" rx="1.5"/><path d="M4.5 6.5V4.5a2.5 2.5 0 015 0v2"/></svg><span>{c.locked}</span></div>}
      <button className="assess-primary-btn" onClick={() => isReassessment ? onPay(scores) : setShowAbout(true)}>{c.cta}</button>
      {!isReassessment && <button className="assess-skip-link" onClick={onSkip}>{c.skip}</button>}
    </div>
  );
}

// ─── MAIN ASSESSMENT ──────────────────────────────────────────────────────────
export default function Assessment({ user, onPay, onSkip, isReassessment = false }) {
  const [lang, setLang] = useState("en");
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [scores, setScores] = useState(null);
  const [saving, setSaving] = useState(false);

  const userName = user?.displayName?.split(" ")[0] || "";
  const t = LABELS[lang];

  const answeredCount = Object.keys(answers).length;
  const totalQ = CATEGORIES.reduce((sum, cat) => sum + cat.questions.length, 0);
  const allAnswered = answeredCount === totalQ;

  const handleSubmit = async () => {
    setSaving(true);
    const catScores = {};
    CATEGORIES.forEach(cat => {
      // Count "yes" answers (value = 1) per category
      catScores[cat.id] = cat.questions.reduce((sum, _, qi) => sum + (answers[`${cat.id}-${qi}`] === 1 ? 1 : 0), 0);
    });
    setScores(catScores);
    try {
      const collectionName = isReassessment ? "reassessments" : "assessments";
      await setDoc(doc(db, collectionName, user.uid), {
        answers, scores: catScores, lang,
        completedAt: new Date().toISOString(),
        userId: user.uid,
        userName: user.displayName || "",
        email: user.email,
      });
    } catch(e) { console.error(e); }
    setSaving(false);
    setSubmitted(true);
  };

  if (submitted && scores) {
    return (
      <div className="assess-page">
        <style>{ASSESS_CSS}</style>
        <div className="assess-lang-row">
          <div className="assess-lang-toggle">
            <button className={lang==="sw"?"active":""} onClick={()=>setLang("sw")}>Swahili</button>
            <button className={lang==="en"?"active":""} onClick={()=>setLang("en")}>English</button>
          </div>
        </div>
        <ResultsScreen scores={scores} lang={lang} userName={userName} onPay={onPay} onSkip={onSkip} isReassessment={isReassessment} />
      </div>
    );
  }

  return (
    <div className="assess-page">
      <style>{ASSESS_CSS}</style>

      <div className="assess-header">
        <div className="assess-lang-row">
          <div className="assess-lang-toggle">
            <button className={lang==="sw"?"active":""} onClick={()=>setLang("sw")}>Swahili</button>
            <button className={lang==="en"?"active":""} onClick={()=>setLang("en")}>English</button>
          </div>
        </div>
        <h2 className="assess-title">{t.title}</h2>
        <p className="assess-sub">{t.sub}</p>
        <div className="assess-progress-row">
          <div className="assess-progress-bar">
            <div className="assess-progress-fill" style={{width:`${(answeredCount/totalQ)*100}%`}}/>
          </div>
          <span className="assess-progress-label">{answeredCount} {t.answeredOf}</span>
        </div>
      </div>

      <div className="assess-questions">
        {CATEGORIES.map((cat) => (
          <div key={cat.id} className="assess-category">
            <div className="assess-cat-header">
              <span className="assess-cat-icon"><CatIcon id={cat.icon}/></span>
              <span className="assess-cat-title">{lang==="sw" ? cat.sw : cat.en}</span>
            </div>
            {cat.questions.map((q, qi) => {
              const key = `${cat.id}-${qi}`;
              const val = answers[key];
              return (
                <div key={qi} className="assess-question">
                  <p className="assess-q-text">{lang==="sw" ? q.sw : q.en}</p>
                  <div className="assess-yesno">
                    <button
                      className={`assess-yes-btn ${val===1?"selected-yes":""}`}
                      onClick={() => setAnswers(prev => ({...prev, [key]: 1}))}
                    >{t.yes}</button>
                    <button
                      className={`assess-no-btn ${val===0?"selected-no":""}`}
                      onClick={() => setAnswers(prev => ({...prev, [key]: 0}))}
                    >{t.no}</button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="assess-footer">
        <button className="assess-primary-btn" onClick={handleSubmit} disabled={!allAnswered || saving}>
          {saving ? t.loading : t.seeResults}
        </button>
        <button className="assess-skip-link" onClick={onSkip}>{t.skip}</button>
      </div>
    </div>
  );
}

const ASSESS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box}
:root{--cream:#F2F6F7;--parchment:#E3EEF0;--bark:#4F9CA8;--soil:#1F4E5A;--stone:#8FA6AD;--text:#1E2E33;--light-text:#5A7480;--border:#D6E2E5}
.assess-page{min-height:100vh;background:linear-gradient(160deg,#DCEBED,#BFD9DE 50%,#A6CDD4);padding:24px 16px 48px;font-family:'Inter',sans-serif;color:var(--text)}
.assess-lang-row{display:flex;justify-content:flex-end;margin-bottom:12px}
.assess-lang-toggle{display:flex;gap:4px}
.assess-lang-toggle button{background:none;border:1px solid var(--border);border-radius:6px;padding:4px 14px;font-family:'Inter',sans-serif;font-size:.78rem;cursor:pointer;color:var(--stone);transition:all .15s}
.assess-lang-toggle button.active{background:var(--soil);color:white;border-color:var(--soil)}
.assess-header{background:var(--cream);border-radius:16px;padding:20px;margin-bottom:12px;box-shadow:0 4px 20px rgba(20,50,58,.1)}
.assess-title{font-family:'Plus Jakarta Sans',serif;font-size:1.4rem;font-weight:600;color:var(--soil);margin-bottom:4px}
.assess-sub{font-size:.82rem;color:var(--light-text);margin-bottom:14px;font-style:italic}
.assess-progress-row{display:flex;align-items:center;gap:10px}
.assess-progress-bar{flex:1;height:6px;background:var(--parchment);border-radius:3px;overflow:hidden}
.assess-progress-fill{height:100%;background:var(--bark);border-radius:3px;transition:width .3s}
.assess-progress-label{font-size:.72rem;color:var(--stone);flex-shrink:0}
.assess-questions{display:flex;flex-direction:column;gap:10px}
.assess-category{background:var(--cream);border-radius:16px;padding:18px;box-shadow:0 2px 12px rgba(20,50,58,.08)}
.assess-cat-header{display:flex;align-items:center;gap:10px;margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid var(--border)}
.assess-cat-icon{font-size:1.2rem}
.assess-cat-title{font-family:'Plus Jakarta Sans',serif;font-size:1.05rem;font-weight:600;color:var(--soil)}
.assess-question{margin-bottom:14px;padding-bottom:14px;border-bottom:1px solid var(--border)}
.assess-question:last-child{margin-bottom:0;padding-bottom:0;border-bottom:none}
.assess-q-text{font-size:.85rem;color:var(--text);line-height:1.6;margin-bottom:10px}
.assess-yesno{display:flex;gap:10px}
.assess-yes-btn,.assess-no-btn{flex:1;padding:10px;border-radius:10px;border:2px solid var(--border);background:white;font-family:'Inter',sans-serif;font-size:.88rem;font-weight:500;cursor:pointer;transition:all .15s;color:var(--stone)}
.assess-yes-btn:hover{border-color:#E8745A;color:#E8745A}
.assess-no-btn:hover{border-color:#6A9E6A;color:#6A9E6A}
.assess-yes-btn.selected-yes{background:#FBF0EE;border-color:#E8745A;color:#E8745A}
.assess-no-btn.selected-no{background:#EDF5EB;border-color:#6A9E6A;color:#6A9E6A}
.assess-footer{background:var(--cream);border-radius:16px;padding:20px;margin-top:12px;box-shadow:0 4px 20px rgba(20,50,58,.1);display:flex;flex-direction:column;gap:10px;align-items:center}
.assess-primary-btn{width:100%;padding:14px;background:var(--soil);color:white;border:none;border-radius:12px;font-family:'Inter',sans-serif;font-size:.95rem;font-weight:500;cursor:pointer;transition:all .2s}
.assess-primary-btn:hover:not(:disabled){background:var(--bark);transform:translateY(-1px)}
.assess-primary-btn:disabled{opacity:.5;cursor:not-allowed;transform:none}
.assess-skip-link{background:none;border:none;color:var(--stone);font-family:'Inter',sans-serif;font-size:.8rem;cursor:pointer;text-decoration:underline;text-underline-offset:3px}
.assess-skip-link:hover{color:var(--bark)}
.assess-back-btn{background:none;border:none;color:var(--bark);font-family:'Inter',sans-serif;font-size:.82rem;cursor:pointer;margin-bottom:16px;text-decoration:underline;text-underline-offset:3px;padding:0}
.results-wrap{display:flex;flex-direction:column;gap:14px}
.results-title{font-family:'Plus Jakarta Sans',serif;font-size:1.7rem;font-weight:600;color:var(--soil);text-align:center}
.overall-score-circle{display:flex;flex-direction:column;align-items:center;gap:6px;background:var(--cream);border-radius:16px;padding:20px;box-shadow:0 2px 12px rgba(20,50,58,.08)}
.overall-score-label{font-size:.75rem;color:var(--stone);text-transform:uppercase;letter-spacing:.06em;text-align:center}
.category-scores{background:var(--cream);border-radius:16px;padding:18px;box-shadow:0 2px 12px rgba(20,50,58,.08);display:flex;flex-direction:column;gap:14px}
.cat-score-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
.cat-score-left{display:flex;align-items:center;gap:10px;flex:1;min-width:0}
.cat-icon{font-size:1.2rem;flex-shrink:0}
.cat-name{display:block;font-size:.82rem;font-weight:500;color:var(--text)}
.cat-label{display:block;font-size:.7rem;font-weight:500}
.cat-score-right{display:flex;align-items:center;gap:8px;flex-shrink:0}
.cat-bar{width:70px;height:6px;background:var(--parchment);border-radius:3px;overflow:hidden}
.cat-bar-fill{height:100%;border-radius:3px;transition:width .8s ease}
.cat-score-num{font-size:.9rem;font-weight:600;min-width:28px;text-align:right}
.cat-out-of{font-size:.65rem;color:var(--stone);font-weight:400}
.insights-box{background:var(--cream);border-radius:16px;padding:20px;box-shadow:0 2px 12px rgba(20,50,58,.08)}
.insights-label{font-size:.7rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.07em;margin-bottom:12px}
.insights-loading{display:flex;align-items:center;gap:10px;color:var(--stone);font-size:.82rem}
.insights-spinner{width:18px;height:18px;border:2px solid var(--parchment);border-top-color:var(--bark);border-radius:50%;animation:aspin .8s linear infinite;flex-shrink:0}
@keyframes aspin{to{transform:rotate(360deg)}}
.insights-text{font-size:.85rem;color:var(--text);line-height:1.8;white-space:pre-wrap}
.insights-locked{background:var(--parchment);border-radius:10px;padding:12px 16px;font-size:.78rem;color:var(--light-text);text-align:center}
.about-wrap{display:flex;flex-direction:column;gap:14px}
.about-title{font-family:'Plus Jakarta Sans',serif;font-size:1.7rem;font-weight:600;color:var(--soil);text-align:center}
.about-sub{font-size:.85rem;color:var(--light-text);text-align:center;font-style:italic}
.about-items{background:var(--cream);border-radius:14px;padding:16px;box-shadow:0 2px 12px rgba(20,50,58,.08);display:flex;flex-direction:column;gap:10px}
.about-item{display:flex;align-items:center;gap:12px;font-size:.85rem;color:var(--text)}
.about-price{background:var(--parchment);border-radius:12px;padding:16px;text-align:center}
.about-price-amt{display:block;font-family:'Plus Jakarta Sans',serif;font-size:2rem;font-weight:600;color:var(--soil)}
.about-price-note{display:block;font-size:.72rem;color:var(--stone);margin-top:2px}
`;
